import { NgClass } from '@angular/common'
import { Component, DestroyRef, effect, HostListener, input, OnInit, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { IntegrationService } from '@app/modules/events/services/integration.service'
import { CreateWorkspace } from '@app/modules/workspaces/interfaces/create-workspace.interface'
import { ParentOrganization, Workspace, WorkspaceCreationData } from '@app/modules/workspaces/interfaces/workspace.interface'
import { WorkspaceService } from '@app/modules/workspaces/services/workspace.service'
import { UploadTypeEnum } from '@app/v2/auth/types/media.type'
import { UploadComponent } from '@app/v2/shared/components/upload/upload.component'
import { IndustrySectorService } from '@app/v2/shared/services/industry-sector.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { AuthService } from '@auth/services/auth.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { debounceTime, distinctUntilChanged, of, switchMap, tap, timer } from 'rxjs'
import { CountrySelectComponent } from '../../shared/components/country-select/country-select.component'
import { BasicInputComponent } from '../../shared/components/inputs/basic-input/basic-input.component'
import { SelectInputComponent } from '../../shared/components/inputs/select-input/select-input.component'
import { SlugComponent } from '../../shared/components/inputs/slug/slug.component'
import { ModalComponent } from '../../shared/components/modal/modal.component'
import { Alert } from '../../shared/enums/alert.enum'
import { AlertService } from '../../shared/services/alert.service'
import { AppService } from '../../shared/services/app.service'
import { Country } from '../../shared/types/country.type'
import { IndustrySector } from '../../shared/types/industry-sector.type'

@Component({
  selector: 'app-add-new-organization-modal',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, ModalComponent, SelectInputComponent, NgClass, BasicInputComponent, SlugComponent, CountrySelectComponent, UploadComponent],
  templateUrl: './add-new-organization-modal.component.html',
  styleUrls: ['./add-new-organization-modal.component.scss']
})
export class AddNewOrganizationModalComponent implements OnInit {
  onClose = output<void>()
  public isTypeSelectionStep = signal<boolean>(true)
  public selectedEntityType = signal<'independent' | 'managed' | null>(null)
  public selectedParentOrg = signal<ParentOrganization | null>(null)
  public searchQuery = signal<string>('')
  public industrySectorId = signal<FormControl>(new FormControl('', [Validators.required]))
  public parentOrganizations = input<ParentOrganization[]>([])
  public parentWorkspaceId = signal<FormControl>(new FormControl(null))
  public form: FormGroup
  public countries: Country[] = []
  public industrySectors = signal<IndustrySector[]>([])
  public processing = false
  public error: string | null = null
  public uploadTypeEnum = UploadTypeEnum
  readonly businessActivityIcon = `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_35743_11773)">
<path d="M15.7656 9.00387C14.024 10.6993 11.012 11.5473 8 11.5473C4.988 11.5473 1.976 10.6993 0.2344 9.00387L0 8.77569V13.7746C0 14.1877 0.168571 14.5839 0.468629 14.876C0.768687 15.1681 1.17565 15.3322 1.6 15.3322H14.4C14.8243 15.3322 15.2313 15.1681 15.5314 14.876C15.8314 14.5839 16 14.1877 16 13.7746V8.77569L15.7656 9.00387Z" fill="currentColor"/>
<path d="M14.4 3.78063H12V3.00185C12 2.38222 11.7471 1.78797 11.2971 1.34982C10.847 0.911675 10.2365 0.665527 9.6 0.665527H6.4C5.76348 0.665527 5.15303 0.911675 4.70294 1.34982C4.25286 1.78797 4 2.38222 4 3.00185V3.78063H1.6C1.17565 3.78063 0.768687 3.94473 0.468629 4.23682C0.168571 4.52892 0 4.92509 0 5.33818V6.57331L1.3656 7.90268C4.28 10.7374 11.72 10.7374 14.6344 7.90268L16 6.57331V5.33818C16 4.92509 15.8314 4.52892 15.5314 4.23682C15.2313 3.94473 14.8243 3.78063 14.4 3.78063ZM8 8.45328C7.84177 8.45328 7.6871 8.4076 7.55554 8.32203C7.42398 8.23646 7.32145 8.11483 7.2609 7.97253C7.20035 7.83022 7.1845 7.67364 7.21537 7.52257C7.24624 7.3715 7.32243 7.23274 7.43431 7.12382C7.5462 7.01491 7.68874 6.94074 7.84393 6.91069C7.99911 6.88064 8.15997 6.89606 8.30615 6.95501C8.45233 7.01395 8.57727 7.11377 8.66518 7.24184C8.75308 7.36991 8.8 7.52047 8.8 7.6745C8.8 7.88105 8.71571 8.07913 8.56569 8.22518C8.41566 8.37123 8.21217 8.45328 8 8.45328ZM10.4 3.78063H5.6V3.00185C5.6 2.79531 5.68429 2.59722 5.83431 2.45117C5.98434 2.30513 6.18783 2.22308 6.4 2.22308H9.6C9.81217 2.22308 10.0157 2.30513 10.1657 2.45117C10.3157 2.59722 10.4 2.79531 10.4 3.00185V3.78063Z" fill="currentColor"/>
</g>
<defs>
<clipPath id="clip0_35743_11773">
<rect width="16" height="16" fill="currentColor"/>
</clipPath>
</defs>
</svg>`
  private _replayTimestamp = signal<number>(Date.now())

  constructor(
    private _fb: FormBuilder,
    private _modalService: ModalService,
    private _workspaceService: WorkspaceService,
    private _appService: AppService,
    private _authService: AuthService,
    private _integrationService: IntegrationService,
    private _router: Router,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    private _industrySectorService: IndustrySectorService,
    private _destroyRef: DestroyRef
  ) {
    effect(() => {
      if (this.parentOrganizations().length === 0) {
        this.isTypeSelectionStep.set(false)
        this.selectedEntityType.set('independent')
      } else {
        this.isTypeSelectionStep.set(true)
        this.selectedEntityType.set(null)
      }
    })
  }

  ngOnInit(): void {
    this.form = this._fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      acronym: ['', [Validators.required], [this._uniqueAcronymValidator()]],
      country: ['', [Validators.required]],
      industrySector: ['', [Validators.required]],
      parentWorkspaceId: this.parentWorkspaceId(),
      organizationLogo: new FormGroup({
        path: new FormControl(null),
        id: new FormControl(null),
        name: new FormControl(null)
      })
    })

    // Load countries and industry sectors
    this._appService
      .getAllCountries()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((response) => {
        this.countries = response.data.countries
      })
    this._loadIndustrySectors()
    this._replayTimestamp.set(Date.now())
  }

  public onEntityTypeSelect(type: 'independent' | 'managed'): void {
    this.selectedEntityType.set(type)

    // Update parentWorkspaceId validation based on entity type
    if (type === 'managed') {
      this.parentWorkspaceId().setValidators([Validators.required])
      this.parentWorkspaceId().updateValueAndValidity()
    } else {
      // Remove required validator for independent entities
      this.parentWorkspaceId().clearValidators()
      this.parentWorkspaceId().updateValueAndValidity()
      this.parentWorkspaceId().setValue(null)
    }
  }

  public goToCreationStep(): void {
    if (!this.selectedEntityType()) return

    if (this.isTypeSelectionStep()) {
      // Check if form is valid before proceeding
      if (this.selectedEntityType() === 'managed' && this.parentWorkspaceId().invalid) {
        this.parentWorkspaceId().markAsTouched()
        return // Don't proceed if managed entity is selected but no parent org is chosen
      }
      this.isTypeSelectionStep.set(false)
    } else {
      // Prevent multiple submissions
      if (this.processing) return
      this._submitForm()
    }
  }

  @HostListener('document:keydown.enter', ['$event'])
  public onEnterKey(event: KeyboardEvent): void {
    this.goToCreationStep()
  }

  public goBackToTypeSelectionStep(): void {
    if (!this.isTypeSelectionStep()) {
      this.isTypeSelectionStep.set(true)
    }
  }

  public onCloseModal(): void {
    this._resetForm()
    this._modalService.toggleModal('add-new-organization-modal')
    this.onClose.emit()
  }

  public isModalOpen(): boolean {
    return this._modalService.isModalOpen('add-new-organization-modal')
  }

  public onParentOrgSelect(org: ParentOrganization): void {
    this.selectedParentOrg.set(org)
    this.searchQuery.set(org.name)
  }

  /**
   * Load industry sectors from the service
   */
  private _loadIndustrySectors(): void {
    this.industrySectors.set(this._industrySectorService.getIndustrySectors())
  }

  /**
   * On industry sector changed
   * @param {number} id Industry sector id
   */
  public onIndustrySectorChanged(id: number): void {
    this.industrySectorId().setValue(id)
    this.form.controls['industrySector'].setValue(id)
  }

  /**
   * On parent workspace changed
   * @param {number} id Parent organization id
   */
  public onParentWorkspaceChanged(id: number): void {
    this.parentWorkspaceId().setValue(id)
    // Find the selected organization and update selectedParentOrg
    const selectedOrg = this.parentOrganizations().find((org) => org.id === id)
    if (selectedOrg) {
      this.selectedParentOrg.set(selectedOrg)
    }
  }

  public onSearchChange(query: string): void {
    this.searchQuery.set(query)
  }

  private _submitForm(): void {
    // Mark all form fields as touched to show validation errors
    this.form.markAllAsTouched()
    this.industrySectorId().markAsTouched()

    if (this.form.valid) {
      if (this._authService.isAdmin) {
        this._alertService.alert(Alert.ERROR, this._translateService.instant('v2.organization.adminCannotCreateOrganization'))
        return
      }
      this.error = null
      this.processing = true

      // Prepare workspace data - include all fields that backend expects
      const workspaceData: WorkspaceCreationData = {
        name: this.form.value.name,
        slug: this.form.value.acronym,
        // 1 for main, 2 for sub
        role_id: this.selectedEntityType() === 'managed' ? 2 : 1,
        country: this.form.value.country,
        email: this._authService.userEmail,
        full_name: this._authService.userDisplayName,
        phone: this._authService.user?.phone_number || '',
        parent: this.selectedEntityType() === 'managed' ? this.selectedParentOrg()?.id : null,
        logo: this.form.value.organizationLogo.path
      }

      // Get country name
      const countryName = this.form.value.country?.name || this.form.value.country

      // Create workspace and update user in parallel
      this._workspaceService
        .createWorkspace(workspaceData as unknown as CreateWorkspace)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (workspace: Workspace) => {
            if (workspace) {
              this._alertService.alert(Alert.SUCCESS, this._translateService.instant('v2.organization.createdSuccessfully'))

              this._authService.workspaces = [...this._authService.workspaces, workspace]

              // Handle parent workspace assignment safely
              let parentWorkspace: Workspace | undefined
              if (this.selectedEntityType() === 'managed' && this.selectedParentOrg()) {
                parentWorkspace = this.selectedParentOrg() as Workspace
              }

              this._authService.currentParentWorkspace = this.selectedEntityType() === 'managed' ? parentWorkspace : workspace
              this._authService.currentWorkspace = workspace
            }

            this._resetForm()
            this.processing = false
            this._modalService.toggleModal('add-new-organization-modal')

            this._router.navigate(['events', 'list'])

            // Send Slack notification
            this._integrationService
              .sendWorkspaceRegistrationNotification({
                workspace,
                user: this._authService.user,
                countryName,
                secondsLookBack: Math.floor((Date.now() - this._replayTimestamp()) / 1000)
              })
              .pipe(takeUntilDestroyed(this._destroyRef))
              .subscribe(() => this._replayTimestamp.set(Date.now()))
          },
          error: (error) => {
            this._resetForm()
            this._modalService.toggleModal('add-new-organization-modal')
            this.processing = false
            this.error = error.message || 'An error occurred while creating the workspace'
            this._alertService.alert(Alert.ERROR, this.error)
          }
        })
    }
  }

  private _resetForm(): void {
    this.isTypeSelectionStep.set(true)
    this.selectedEntityType.set(null)
    this.selectedParentOrg.set(null)
    this.searchQuery.set('')
    this.parentWorkspaceId().setValue(null)
    this.parentWorkspaceId().clearValidators()
    this.parentWorkspaceId().updateValueAndValidity()
    this.industrySectorId().setValue('')
    this.industrySectorId().markAsUntouched()
    this.industrySectorId().markAsPristine()
    this.form.reset()
    this.processing = false
    this.error = null
  }

  /**
   * Async validator to check if a slug is already taken
   * @param control Form control
   * @returns AsyncValidatorFn
   */
  private _uniqueAcronymValidator(): AsyncValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value || control.value.length < 3) return of(null)
      return timer(300).pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(() => this._workspaceService.checkForDuplicateSlug(control.value)),
        tap(() => setTimeout(() => this.form.updateValueAndValidity()))
      )
    }
  }
}
